import { createApiRef } from '@backstage/frontend-plugin-api';

export interface TenantInfo {
  id: string;
  defaultDomain?: string;
  name?: string;
  clientId?: string;
  openIdConfigUrl?: string;
  integrationDomain?: string;
}

export enum Environment {
  DEV = "DEV",
  TST = "TST",
  STA = "STA",
  PRD = "PRD",
};

export enum UriType {
  WEB = "web",
  SPA = "spa",
};

export enum MatchType {
  PATH = "path",
  PATHSEPARATEDPREFIX = "pathSeparatedPrefix",
  REGEX = "regex",
  PREFIX = "prefix",
};

export enum RewriteType {
  NONE = "none",
  PREFIXREWRITE = "prefixRewrite",
  REGEXREWRITE = "regexRewrite",
};

export enum CookieMatchType {
  EXACT = "exact",
  SAFEREGEX = "safeRegex",
};

export interface CookieMatcher {
  name: string;
  cookieMatchType: CookieMatchType;
  cookieMatchValue: string;
  ignoreCase?: boolean;
}

export interface RedirectUris {
  uris: { type: UriType, uri: string; }[];
}

export interface Idps {
  idps: Idp[];
}

export interface Idp {
  id: string;
  domain: string;
  aliasDomains: string[];
  oidc: Oidc;
  scopes: string[];
  claimMappings: ClaimMapping[];
  customer: Customer;
  cloudPortalRolesMigrated: boolean;
  notes: string;
}

export interface Customer {
  shortCode: string;
  accountName: string;
}

export interface Oidc {
  clientId: string;
  clientSecretName: string;
  tokenEndpoint: string;
  authorizationEndpoint: string;
  authenticationType: string;
}

export type TokenType =
  | 'idToken'
  | 'accessToken';

export interface ClaimMapping {
  type: TokenType;
  to: string;
  from: string;
  clientCredentialsFrom?: string;
}

export interface ScIdpRole {
  name: string;
  displayName: string;
  allowedMemberType: string;
  description: string;
  enableRole: boolean;
  assignedAdGroups: string[];
}
export interface TenantsAndSystems {
  tenants: Tenant[];
  systems: SystemDomain[];
}

export interface Tenant {
  name: string;
  systems: string[];
}

export interface SystemDomain {
  domain: string;
  authenticated: Rule[];
  unauthenticated: Rule[];
}

export interface Rule {
  pathPrefix: string;
}

export interface AuthorizedSystem extends SystemDomain {
  tenants: string[];
}

export interface VirtualHost {
  domain: string;
  envoyClusterHosts: EnvoyClusterHost[]
}

export interface EnvoyClusterHost {
  host: string;
  routeEntries: RouteEntry[];
  healthCheckPath: string;
}

export interface RouteEntry {
  matchType: MatchType;
  match: string;
  rewriteType: RewriteType;
  rewrite?: string;
  substitution?: string;
  cookies?: CookieMatcher[];
  useCustomTimeout: boolean;
  timeoutSeconds?: number;
}

export interface RegexAndSubstitution {
  regex: string;
  substitution: string;
}

export interface B2CApi {
  getRedirectUris(options: {
    environment: Environment;
  }): Promise<RedirectUris>;
  getIdpConfig(options: {
    environment: Environment;
  }): Promise<Idps>;
  getTenantInfo(options: {
    environment: Environment;
  }): Promise<TenantInfo>;
  getScIdpRoles(): Promise<ScIdpRole[]>;
  getAuthorizedTenantsAndSystems(options: {
    environment: Environment;
  }): Promise<AuthorizedSystem[]>;
  getVirtualHosts(options: {
    environment: Environment;
  }): Promise<VirtualHost[]>;
}

export interface B2CTenantConfig {
  tenantId: string;
  clientId?: string;
  clientSecret?: string;
  domain: string;
}

export const b2cApiRef = createApiRef<B2CApi>().with({
  id: 'plugin.b2c.service',
  pluginId: 'b2c-frontend',
});
